import { z } from 'zod'
import { useZorm } from 'react-zorm'
import {
    FormField,
    fieldDescription,
    withProps,
} from '@codecorp/zorm-conventions'
import { ShowBarcode } from '../components/show-barcode'
import { CreatorButtons } from '../components/creator-buttons'
import { Pin, ageVerification } from '@codecorp/barcode-data-generator'
import { useFormReset } from '../hooks/use-form'
import { useShowModal } from '../hooks/use-show-modal'
import { useState } from 'react'

const zormBool = z
    .enum(['on'])
    .optional()
    .transform((x) => x === 'on')

const zormInt = z
    .string()
    .regex(/^\d{1,3}$|^$/)
    .transform((x) => (x != null && x !== '' ? Number.parseInt(x) : undefined))
    .describe(
        fieldDescription({ inputType: 'number', placeHolder: 'Enter Age' })
    )

const FormSchema = z
    .object({
        label: z.string().describe(
            fieldDescription({
                placeHolder: 'This name will be placed under the QR Code.',
            })
        ),
        shouldSetTime: zormBool.describe(
            fieldDescription({ label: 'Set Time' })
        ),
        shouldLock: z
            .enum(['on'])
            .optional()
            .transform((x) => (x === 'on' ? true : null))
            .describe(
                fieldDescription({
                    label: 'Lock Scanner',
                })
            ),
        pin: z
            .string()
            .regex(/^$|^\d{4,8}$/)
            .optional()
            .transform((x) => (x !== '' ? x : null))
            .describe(
                fieldDescription({
                    label: 'PIN',
                    tooltip: 'Please enter a PIN between 4 to 8 digits.',
                    placeHolder: 'Enter PIN',
                })
            ),
        enableAgeVerification: z
            .enum(['on', 'off', 'noAction'])
            .transform((x) => (x === 'on' ? true : x === 'off' ? false : null))
            .describe(
                fieldDescription({
                    labels: ['Enable', 'Disable', 'No Action'],
                    label: 'Age Verification',
                })
            ),
        underageThreshold: zormInt,
        overageThreshold: zormInt,
        enableExpirationVerification: z
            .enum(['on', 'off', 'noAction'])
            .optional()
            .transform((x) => (x === 'on' ? true : x === 'off' ? false : null))
            .describe(
                fieldDescription({
                    labels: ['Enable', 'Disable', 'No Action'],
                    label: 'Expiration Verification',
                })
            ),
        pdf417OutputMode: z
            .enum(['enableAll', 'enableNoDL', 'disable'])
            .optional()
            .describe(
                fieldDescription({
                    labels: [
                        'Always Enable output of PDF17 barcode data',
                        "Enable output for non-driver's license PDF417 barcodes only",
                        'Disable output of PDF417 barcode data',
                    ],
                    inputType: 'select',
                    className: 'sm:w-96',
                })
            ),
    })
    .refine(
        (data) => data.shouldLock == null || data.pin != null,
        'PIN must be given if lock is enabled'
    )
    .refine(
        (data) =>
            data.enableAgeVerification !== true ||
            data.underageThreshold != null ||
            data.overageThreshold != null,
        'An age threshold must be given if age verification is enabled'
    )

export const AgeVerificationBarcodeCreator = () => {
    const { setSubmittedBarcode, handleFormReset } = useFormReset(undefined)
    const showModal = useShowModal()

    // Hook to track if Age Verification is enabled or disabled
    const [isAgeVerificationEnabled, setIsAgeVerificationEnabled] = useState<
        boolean | null
    >(null)

    const zo = useZorm('ageVerificationBarcode', FormSchema, {
        onValidSubmit: async ({ preventDefault, data }) => {
            preventDefault()
            setSubmittedBarcode(data)
            showModal(
                <ShowBarcode
                    label={data.label ?? ''}
                    barcode={ageVerification({
                        ...data,
                        pin: data.pin as Pin,
                        shouldLock: data.shouldLock,
                    })}
                />
            )
        },
    })

    // Handler for Age Verification change
    const handleAgeVerificationChange = (event: string) => {
        const value = event === 'on' ? true : event === 'off' ? false : null
        setIsAgeVerificationEnabled(value)
    }

    return (
        <div>
            <form
                ref={zo.ref}
                className="ml-10 mr-10 leading-10"
                onReset={handleFormReset}
            >
                {zo.fields.label(FormField)}
                {zo.fields.shouldSetTime(FormField)}
                {zo.fields.shouldLock(FormField)}
                {zo.fields.pin(FormField)}

                {/* Age Verification Field */}
                {zo.fields.enableAgeVerification(
                    withProps(
                        {
                            defaultValue: 'noAction',
                            onChange: (e) => handleAgeVerificationChange(e), // Add the onChange handler
                        },
                        FormField
                    )
                )}

                {zo.fields.underageThreshold(FormField)}
                {zo.fields.overageThreshold(FormField)}

                {/* Expiration Verification Field */}
                {zo.fields.enableExpirationVerification(
                    withProps(
                        {
                            defaultValue: 'noAction',
                            disabled: isAgeVerificationEnabled === false,
                        },
                        FormField
                    )
                )}

                {/* PDF417 Output Mode Field with dynamic disabling */}
                {zo.fields.pdf417OutputMode(
                    withProps(
                        {
                            defaultValue: 'disable',
                            disabled: isAgeVerificationEnabled === false, // Disable if Age Verification is disabled
                        },
                        FormField
                    )
                )}

                <p className="text-pink-600">{zo.errors()?.message}</p>
                <CreatorButtons />
            </form>
        </div>
    )
}
